import React, { useState, useEffect } from 'react';
import { User } from '../../services/authService';
import { getActivities, Activity } from '../../services/activityService';
import PageHeader from '../common/PageHeader';
import './ActivitiesPage.css';

interface ActivitiesPageProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

type DateFilter = 'today' | 'this-week' | 'this-month' | 'all-time' | 'custom';

interface GroupedActivities {
  [key: string]: Activity[];
}

const ActivitiesPage: React.FC<ActivitiesPageProps> = ({ user, onToggleSidebar }) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState<DateFilter>('today');
  const [actionTypeFilter, setActionTypeFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Get unique action types from activities
  const actionTypes = Array.from(new Set(activities.map(a => a.action_type))).sort();

  useEffect(() => {
    if (user) {
      loadActivities();
    }
  }, [user, dateFilter, actionTypeFilter, customStartDate, customEndDate]);

  // Format date to YYYY-MM-DD using local timezone (not UTC)
  const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDateRange = (filter: DateFilter): { startDate?: string; endDate?: string } => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (filter) {
      case 'today':
        const todayStr = formatLocalDate(today);
        return { startDate: todayStr, endDate: todayStr };
      case 'this-week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        return {
          startDate: formatLocalDate(weekStart),
          endDate: formatLocalDate(today)
        };
      case 'this-month':
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        return {
          startDate: formatLocalDate(monthStart),
          endDate: formatLocalDate(today)
        };
      case 'custom':
        return {
          startDate: customStartDate || undefined,
          endDate: customEndDate || undefined
        };
      case 'all-time':
      default:
        return {};
    }
  };

  const loadActivities = async () => {
    try {
      setLoading(true);
      const dateRange = getDateRange(dateFilter);
      const filters = {
        ...dateRange,
        actionType: actionTypeFilter || undefined,
        limit: 500
      };
      const data = await getActivities(filters);
      setActivities(data);
    } catch (error) {
      console.error('Error loading activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const groupActivitiesByDate = (activities: Activity[]): GroupedActivities => {
    const grouped: GroupedActivities = {};
    
    // Sort activities by date (newest first) before grouping
    const sortedActivities = [...activities].sort((a, b) => {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
    
    sortedActivities.forEach(activity => {
      const date = new Date(activity.created_at);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const activityDate = new Date(date);
      activityDate.setHours(0, 0, 0, 0);
      
      let dateKey: string;
      const diffTime = today.getTime() - activityDate.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) {
        dateKey = 'Today';
      } else if (diffDays === 1) {
        dateKey = 'Yesterday';
      } else {
        dateKey = date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
      }
      
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(activity);
    });
    
    return grouped;
  };

  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDetails = (details: object): React.ReactNode => {
    if (!details || Object.keys(details).length === 0) {
      return null;
    }

    // Format common fields in a human-friendly way
    const formatValue = (key: string, value: any): string => {
      if (key === 'tag_names' && Array.isArray(value)) {
        return value.join(', ');
      }
      if (key === 'assigned_date' || key === 'date') {
        return new Date(value).toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        });
      }
      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      }
      if (value === null || value === undefined) {
        return '';
      }
      return String(value);
    };

    const formatKey = (key: string): string => {
      // Convert snake_case to Title Case
      return key
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    };

    return (
      <div className="activities-item-details">
        {Object.entries(details).map(([key, value]) => {
          const formattedValue = formatValue(key, value);
          if (formattedValue === '' || formattedValue === null || formattedValue === undefined) {
            return null;
          }
          
          return (
            <div key={key} className="activities-detail-row">
              <span className="activities-detail-label">{formatKey(key)}:</span>
              <span className="activities-detail-value">{formattedValue}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const groupedActivities = groupActivitiesByDate(activities);
  // Get date keys and ensure Today/Yesterday are first, others maintain insertion order
  // (which is already sorted since we sorted activities before grouping)
  const dateKeys = Object.keys(groupedActivities).sort((a, b) => {
    if (a === 'Today') return -1;
    if (b === 'Today') return 1;
    if (a === 'Yesterday') return -1;
    if (b === 'Yesterday') return 1;
    // For other dates, maintain insertion order (already sorted)
    return 0;
  });

  return (
    <div className="activities-page">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={
          <h1 className="activities-page-title">/activities</h1>
        }
        rightActions={
          <>
            <div className="activities-filter-container">
              <button 
                className="activities-filter-btn"
                onClick={() => setShowFilters(!showFilters)}
                title="Filters"
              >
                ⋯
              </button>
              {showFilters && (
                <div className="activities-filter-dropdown">
                  <div className="activities-filter-group">
                    <label className="activities-filter-label">Date Range</label>
                    <select
                      className="activities-filter-select"
                      value={dateFilter}
                      onChange={(e) => setDateFilter(e.target.value as DateFilter)}
                    >
                      <option value="today">Today</option>
                      <option value="this-week">This Week</option>
                      <option value="this-month">This Month</option>
                      <option value="all-time">All Time</option>
                      <option value="custom">Custom Range</option>
                    </select>
                  </div>
                  
                  {dateFilter === 'custom' && (
                    <div className="activities-filter-group">
                      <label className="activities-filter-label">Start Date</label>
                      <input
                        type="date"
                        className="activities-filter-input"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                      />
                      <label className="activities-filter-label">End Date</label>
                      <input
                        type="date"
                        className="activities-filter-input"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                      />
                    </div>
                  )}
                  
                  <div className="activities-filter-group">
                    <label className="activities-filter-label">Action Type</label>
                    <select
                      className="activities-filter-select"
                      value={actionTypeFilter}
                      onChange={(e) => setActionTypeFilter(e.target.value)}
                    >
                      <option value="">All Types</option>
                      {actionTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  
                  <button
                    className="activities-filter-close"
                    onClick={() => setShowFilters(false)}
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </>
        }
      />

      <div className="activities-page-content">
        {loading ? (
          <div className="activities-loading">Loading...</div>
        ) : activities.length === 0 ? (
          <div className="activities-empty">No activities found</div>
        ) : (
          <div className="activities-list">
            {dateKeys.map(dateKey => (
              <div key={dateKey} className="activities-date-group">
                <h2 className="activities-date-header">{dateKey}</h2>
                <div className="activities-date-items">
                  {groupedActivities[dateKey].map((activity, index) => (
                    <React.Fragment key={activity.id}>
                      <div className="activities-item">
                        <div className="activities-item-header">
                          <span className="activities-item-time">{formatTime(activity.created_at)}</span>
                          <span className="activities-item-type">{activity.action_type}</span>
                        </div>
                        {formatDetails(activity.details)}
                      </div>
                      {index < groupedActivities[dateKey].length - 1 && (
                        <div className="activities-item-divider"></div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivitiesPage;

